import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, FilterFn } from '@tanstack/react-table';

import { ProjectTypeDtoIn } from '@/models/in/ProjectTypeDtoIn';
import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import EditOrCreateProjectTypeDialog from './EditOrCreateProjectTypeDialog';
import DeleteProjectTypeDialog from './DeleteProjectTypeDialog';

const QUERY_URL = 'management/project-types';

const projectTypeSearch: FilterFn<ProjectTypeDtoIn> = (row, _columnId, value) => {
  if (!value) return true;
  const searchValue = String(value).toLowerCase();
  const { name, description } = row.original;
  return [name, description].some((field) => field?.toLowerCase().includes(searchValue));
};

/**
 * Admin CRUD for the closed list of project types (סוגי פרויקטים) — the selectable cards in
 * step 1 of the coordinator's new-project wizard. Deleting archives the type: projects already
 * referencing it keep resolving its name, it just stops being offered.
 */
const ProjectTypesManagementPage = () => {
  const { t } = useTranslation();

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [typeToEdit, setTypeToEdit] = useState<ProjectTypeDtoIn | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState<ProjectTypeDtoIn | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: projectTypes, isLoading, refetch } = useGetAuthApi<ProjectTypeDtoIn[]>(QUERY_URL);

  const handleOpenCreateDialog = useCallback(() => {
    setTypeToEdit(null);
    setIsEditDialogOpen(true);
  }, []);

  const handleOpenEditDialog = useCallback((projectType: ProjectTypeDtoIn) => {
    setTypeToEdit(projectType);
    setIsEditDialogOpen(true);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setIsEditDialogOpen(false);
    setTypeToEdit(null);
  }, []);

  const handleOpenDeleteDialog = useCallback((projectType: ProjectTypeDtoIn) => {
    setTypeToDelete(projectType);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setTypeToDelete(null);
  }, []);

  const columns: ColumnDef<ProjectTypeDtoIn>[] = [
    {
      id: 'name',
      header: Col(t('projectTypesManagement.name')),
      accessorKey: 'name',
      enableSorting: true,
      meta: {
        className: 'min-w-[220px]',
      },
    },
    {
      id: 'description',
      header: Col(t('projectTypesManagement.description')),
      accessorKey: 'description',
      enableSorting: false,
      meta: {
        className: 'min-w-[300px]',
      },
    },
    {
      id: 'actions',
      header: Col(t('projectTypesManagement.actions')),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditDialog(row.original);
            }}
          >
            {t('projectTypesManagement.edit')}
          </Button>
          <Button
            variant="outline"
            className="!text-red-500 !border-red-300"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDeleteDialog(row.original);
            }}
          >
            {t('projectTypesManagement.delete')}
          </Button>
        </div>
      ),
      meta: {
        className: 'min-w-[200px]',
      },
    },
  ];

  return (
    <div>
      <DataTable
        title={t('projectTypesManagement.title')}
        data={projectTypes ?? []}
        isLoading={isLoading}
        columns={columns}
        filters={
          <div className="flex items-center justify-between gap-3">
            <Button variant="primary" onClick={handleOpenCreateDialog}>
              {t('projectTypesManagement.addType')}
            </Button>
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="projectTypesManagement.search"
            />
          </div>
        }
        emptyStateContent={{ title: t('projectTypesManagement.noData'), description: t('projectTypesManagement.noDataDescription') }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customGlobalSearch={projectTypeSearch}
      />

      <EditOrCreateProjectTypeDialog
        open={isEditDialogOpen}
        onClose={handleCloseEditDialog}
        typeToEdit={typeToEdit}
        onSuccess={refetch}
      />

      {isDeleteDialogOpen && typeToDelete && (
        <DeleteProjectTypeDialog
          open={isDeleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          projectType={typeToDelete}
          onSuccess={refetch}
        />
      )}
    </div>
  );
};

export default ProjectTypesManagementPage;
